import { LineChart, Line, XAxis, YAxis, ResponsiveContainer } from 'recharts'

interface PriceChartProps {
  data?: Array<{ date: string; value: number }>
  seed?: number
}

export const PriceChart = ({ data, seed }: PriceChartProps) => {
  // Generate random but consistent data for each chart
  const generateRandomData = (seed: number = 0) => {
    const days = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']
    
    // Predefined patterns for variety
    const patterns = [
      [45, 42, 38, 40, 43, 41, 44], // Pattern 1: dip then rise
      [40, 45, 43, 38, 42, 46, 44], // Pattern 2: up then down
      [38, 42, 45, 43, 40, 44, 47], // Pattern 3: gradual rise
      [47, 44, 40, 38, 41, 43, 45], // Pattern 4: drop then recover
      [42, 38, 40, 45, 43, 46, 44], // Pattern 5: varied
      [44, 46, 43, 40, 38, 41, 45], // Pattern 6: peak then valley
      [40, 43, 45, 42, 38, 44, 47], // Pattern 7: wave pattern
      [45, 40, 38, 42, 46, 43, 44], // Pattern 8: varied
    ]
    
    // Select pattern based on seed and add small variation
    const patternIndex = seed % patterns.length
    const basePattern = patterns[patternIndex]
    const offset = (seed % 10) - 5 // -5 to +5 offset
    
    return days.map((day, i) => ({ 
      day, 
      value: basePattern[i] + offset 
    }))
  }

  // Use data as-is if it has 'day' key, otherwise transform from 'date'
  const chartData = data && data.length > 0 
    ? data.map(item => ({
        day: item.date || 'Mon',
        value: item.value
      }))
    : generateRandomData(seed || 0)

  // Calculate domain to show variations clearly
  const values = chartData.map(d => d.value)
  const minValue = Math.min(...values)
  const maxValue = Math.max(...values)
  const range = maxValue - minValue
  // Add small padding for better visibility
  const padding = range > 0 ? range * 0.15 : 5
  const domainMin = Math.max(0, minValue - padding)
  const domainMax = maxValue + padding

  // Get stroke color based on theme
  const isDarkMode = typeof document !== 'undefined' && document.documentElement.classList.contains('dark')
  const strokeColor = isDarkMode ? '#878787' : '#2d2923'

  return (
    <div className="h-[60px] w-full relative">
      {/* Chart area - 28px height */}
      <div className="absolute left-0 top-0 w-full h-7">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={chartData} margin={{ top: 0, right: 0, left: 0, bottom: 0 }}>
            <XAxis hide={true} />
            <YAxis 
              hide={true}
              domain={[domainMin, domainMax]}
            />
            <Line 
              type="monotone" 
              dataKey="value" 
              stroke={strokeColor}
              strokeWidth={2}
              dot={false}
              isAnimationActive={false}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
      
      {/* Horizontal line at y=40px */}
      <div className="absolute left-0 right-0 top-10 h-px bg-[rgba(45,41,35,0.12)] dark:bg-[rgba(253,253,253,0.12)]" />
      
      {/* Days of week labels at top: 48px */}
      <div 
        className="absolute left-0 top-12 w-full flex items-center gap-[15px] font-plus-jakarta font-normal text-[11px] leading-3 text-[#878787] text-center"
      >
        {chartData.map((item, index) => (
          <span key={index} className="flex-1">
            {item.day}
          </span>
        ))}
      </div>
    </div>
  )
}

